import { useEffect, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, Minus } from "lucide-react";
import { PLANS, type PlanKey } from "@dishfolio/contracts";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, cn, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { clearPlanIntent, planIntent } from "../lib/intent.ts";
import { useOrg } from "../lib/org.tsx";
import { dateTime } from "../lib/format.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError } from "../components/Upgrade.tsx";

const FEATURES: Array<{ label: string; value: (k: PlanKey) => string | boolean }> = [
  { label: "Published menus", value: (k) => (k === "business" ? "Unlimited*" : String(PLANS[k].maxPublishedMenus)) },
  { label: "Locations", value: (k) => String(PLANS[k].maxRestaurants) },
  { label: "Themes", value: (k) => (PLANS[k].themes === "all" ? "All 6" : "Basic") },
  { label: "Drag-and-drop editor", value: (k) => PLANS[k].editor === "full" },
  { label: "Languages", value: (k) => (PLANS[k].maxAdditionalLanguages >= 30 ? "Unlimited" : `+${PLANS[k].maxAdditionalLanguages}`) },
  { label: "AI translation & imports", value: (k) => (PLANS[k].monthlyCredits ? `${PLANS[k].monthlyCredits} credits / mo` : false) },
  { label: "QR codes", value: (k) => ({ standard: "Standard", custom: "Custom style", table: "Per table" })[PLANS[k].qr] },
  { label: "Analytics", value: (k) => ({ basic: "Views", detailed: "Detailed", advanced: "Advanced + orders" })[PLANS[k].analytics] },
  { label: "Table ordering", value: (k) => PLANS[k].tableOrdering },
  { label: "Custom domain", value: (k) => PLANS[k].customDomain },
  { label: "Staff accounts", value: (k) => String(PLANS[k].maxMembers) },
];

export function BillingPage() {
  const { org, refresh } = useOrg();
  const search = useSearch({ from: "/app/billing" });
  const qc = useQueryClient();
  const [loading, setLoading] = useState<string | null>(null);
  const sub = org?.subscription;
  const intent = org?.plan.key === "free" ? planIntent() : null;
  useEffect(() => {
    if (search.checkout !== "success") return;
    clearPlanIntent();
    // Entitlements come from Polar webhooks; ask for a re-sync in case the webhook is still in flight.
    toast.success("Thanks! Your plan updates as soon as Polar confirms the payment.");
    void api
      .post("/billing/sync")
      .catch(() => undefined)
      .finally(() => void refresh());
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: ["org"] }), 3000);
    const stop = setTimeout(() => clearInterval(t), 30_000);
    return () => (clearInterval(t), clearTimeout(stop));
  }, [search.checkout]);
  const checkout = async (plan: "pro" | "business") => {
    setLoading(plan);
    try {
      const res = await api.post<{ url: string }>("/billing/checkout", { plan });
      window.location.href = res.url;
    } catch (err) {
      notifyError(err);
      setLoading(null);
    }
  };
  const portal = async () => {
    setLoading("portal");
    try {
      const res = await api.post<{ url: string }>("/billing/customer-portal");
      window.location.href = res.url;
    } catch (err) {
      notifyError(err);
      setLoading(null);
    }
  };
  if (!org || !sub) return null;
  return (
    <PageContainer wide>
      <PageHeader
        title="Plan & billing"
        description="Billing is handled securely by Polar. Change or cancel any time."
        actions={
          sub.status !== "none" ? (
            <Button variant="outline" onClick={portal} loading={loading === "portal"}>
              Manage subscription <ExternalLink />
            </Button>
          ) : null
        }
      />
      <Card className="mb-6 flex-row flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-sm text-muted-foreground">Current plan</div>
          <div className="flex items-center gap-2 font-display text-2xl font-semibold">
            {org.plan.name}
            {sub.complimentary ? (
              <Badge variant="success">Complimentary</Badge>
            ) : sub.status !== "none" ? (
              <Badge variant={sub.status === "active" || sub.status === "trialing" ? "success" : "warning"}>{sub.status.replace("_", " ")}</Badge>
            ) : null}
          </div>
          {sub.complimentary ? <div className="text-sm text-muted-foreground">Owned by a Dishfolio admin: every feature is included at no charge.</div> : null}
          {sub.currentPeriodEnd ? (
            <div className="text-sm text-muted-foreground">
              {sub.cancelAtPeriodEnd ? "Ends" : "Renews"} {dateTime(sub.currentPeriodEnd)}
            </div>
          ) : null}
          {sub.status === "past_due" ? <div className="text-sm text-destructive">Your last payment failed. Update your payment method to keep your plan.</div> : null}
        </div>
        {!sub.billingConfigured ? <p className="max-w-sm text-xs text-muted-foreground">Billing isn’t configured on this server yet (missing Polar credentials). Plans can’t be purchased until it is.</p> : null}
      </Card>
      <div className="grid gap-4 md:grid-cols-3">
        {(["free", "pro", "business"] as const).map((k) => {
          const p = PLANS[k];
          const current = org.plan.key === k;
          return (
            <Card key={k} className={cn("gap-4", (current || intent === k) && "border-primary ring-2 ring-primary/15", k === "pro" && "md:-translate-y-1 md:shadow-md")}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  {p.name} {intent === k ? <Badge>Your pick</Badge> : k === "pro" ? <Badge>Most popular</Badge> : null}
                </CardTitle>
                <div className="font-display text-3xl font-semibold">
                  ${p.priceUsdCents / 100}
                  <span className="text-sm font-normal text-muted-foreground"> / month</span>
                </div>
                <CardDescription>{k === "free" ? "One beautiful menu, free forever." : k === "pro" ? "All themes, imports and AI translation." : "Ordering, multiple locations and custom domains."}</CardDescription>
              </CardHeader>
              <ul className="grid gap-2 text-sm">
                {FEATURES.map((f) => {
                  const v = f.value(k);
                  return (
                    <li key={f.label} className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">{f.label}</span>
                      {v === true ? <Check className="size-4 text-success" /> : v === false ? <Minus className="size-4 text-muted-foreground" /> : <span className="font-medium">{v}</span>}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-auto">
                {current || sub.complimentary ? (
                  <Button variant="outline" className="w-full" disabled>
                    {current ? "Current plan" : "Included"}
                  </Button>
                ) : k === "free" ? (
                  sub.status !== "none" ? (
                    <Button variant="ghost" className="w-full" onClick={portal}>
                      Cancel in portal
                    </Button>
                  ) : null
                ) : sub.status !== "none" && sub.status !== "expired" && sub.status !== "refunded" ? (
                  <Button className="w-full" variant="outline" onClick={portal} loading={loading === "portal"}>
                    Switch in portal
                  </Button>
                ) : (
                  <Button className="w-full" onClick={() => checkout(k)} loading={loading === k} disabled={!sub.billingConfigured}>
                    Upgrade to {p.name}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        * Fair use: up to {PLANS.business.maxMenus} menus and {PLANS.business.maxRestaurants} locations. Downgrading never deletes menus — extra menus stay online but become read-only. AI credits reset monthly and unused credits don’t roll over. Prices in USD; taxes are calculated by Polar at checkout.
      </p>
    </PageContainer>
  );
}
